import React, { useCallback, useEffect, useState } from "react";
import { ImageIcon, Plus, Pencil, History } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import CatalogTable from "../components/CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "../components/CatalogStateCard";
import EntityFormDialog from "../components/EntityFormDialog";
import StatusBadge from "../components/StatusBadge";
import StatusSelectField, { FIVE_STATE_STATUS_VALUES } from "../components/StatusSelectField";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";
import { createImage, createImageVersion, getImages, updateImageMetadata } from "../../../lib/emailMarketingApi";

/**
 * ImagesPanel — EMAILS-IMPL-009
 *
 * Real multipart file upload against the merged EMAILS-IMPL-008 backend
 * (createImage/createImageVersion in lib/emailMarketingApi.js, both
 * FormData-based). Nothing here derives/guesses content_type, width,
 * height, or file_size -- those are all read back from the backend's own
 * response (email_marketing_image_service.probe_image_bytes probes the
 * ACTUAL uploaded bytes server-side; this component never trusts or
 * displays a client-computed guess as if it were authoritative).
 *
 * PREVIEW (checkpoint §16): no backend endpoint exists to SERVE a stored
 * Image back as a renderable URL (EMAILS-IMPL-008 added only create/
 * version/patch, no GET/retrieve route) -- inventing a direct provider
 * URL from `stored_file_ref` would leak Emergent storage internals and
 * likely not even resolve for a browser. Per the checkpoint's own explicit
 * instruction ("show metadata/file identity and report preview delivery
 * as a future seam"), this component instead shows a LOCAL, client-side
 * preview of the file the operator just selected/uploaded THIS session
 * (via `URL.createObjectURL` on the browser's own File object) -- clearly
 * a "you just picked this" preview, not a "here is what the server has
 * stored" fetch. It is not persisted and disappears on reload, same as
 * this panel's session-only catalog list (see useEmailCatalogEntityList's
 * own note). A real stored-object preview is a future backend seam (a
 * safe serve/redirect endpoint), tracked in the completion report's Open
 * Issues, not fabricated here.
 */
const STATUS_VALUES = FIVE_STATE_STATUS_VALUES;

function FileField({ id, file, onChange, testId }) {
  return (
    <Input
      id={id}
      type="file"
      accept="image/png,image/jpeg,image/webp,image/svg+xml"
      onChange={(e) => onChange(e.target.files?.[0] || null)}
      required={!file}
      data-testid={testId}
    />
  );
}

export default function ImagesPanel() {
  // See AssetFamilyPanel.jsx's own comment: these must be stable
  // identities (useCallback, not fresh inline closures) or `refresh`
  // retriggers its own effect every render.
  //
  // EMAILS-IMPL-009A: `list` now calls the real persistent catalog
  // (getImages()) instead of EMAILS-IMPL-009's `async () => []` stub --
  // Images created in an earlier session/by another user now reappear on
  // mount, with their real stored_file_ref/content_type/alt_text/
  // width/height/file_size from the backend (never a client guess, same
  // as create/new-Version already guaranteed).
  const listFn = useCallback(() => getImages(), []);
  const createFn = useCallback(({ name, status, altText, file }) => createImage({ name, status, altText, file }), []);
  const updateFn = useCallback((id, { name, status }) => updateImageMetadata(id, { name, status }), []);
  const createVersionFn = useCallback((id, { version, altText, file }) => createImageVersion(id, { version, altText, file }), []);
  const getIdFn = useCallback((row) => row.image_id, []);

  const { items, loading, error, mutating, mutationError, clearMutationError, createItem, updateItem, createVersionItem } =
    useEmailCatalogEntityList({
      list: listFn,
      create: createFn,
      update: updateFn,
      createVersion: createVersionFn,
      getId: getIdFn,
    });

  // image_id -> local blob preview URL for the file THIS session just
  // uploaded (see module docstring). Revoked on unmount to avoid leaking
  // object URLs.
  const [previewUrls, setPreviewUrls] = useState({});
  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", status: STATUS_VALUES[0], altText: "", file: null });
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", status: STATUS_VALUES[0] });
  const [versioningRow, setVersioningRow] = useState(null);
  const [versionForm, setVersionForm] = useState({ version: "", altText: "", file: null });

  const openCreate = () => {
    setCreateForm({ name: "", status: STATUS_VALUES[0], altText: "", file: null });
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      const created = await createItem({ name: createForm.name.trim(), status: createForm.status, altText: createForm.altText, file: createForm.file });
      if (createForm.file) {
        setPreviewUrls((prev) => ({ ...prev, [created.image_id]: URL.createObjectURL(createForm.file) }));
      }
      setCreateOpen(false);
    } catch {
      // mutationError already surfaced; keep dialog open
    }
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setEditForm({ name: row.name, status: row.status });
    clearMutationError();
  };

  const submitEdit = async () => {
    try {
      await updateItem(editingRow.image_id, {
        name: editForm.name.trim() !== editingRow.name ? editForm.name.trim() : undefined,
        status: editForm.status !== editingRow.status ? editForm.status : undefined,
      });
      setEditingRow(null);
    } catch {
      // dialog stays open
    }
  };

  const openVersion = (row) => {
    setVersioningRow(row);
    setVersionForm({ version: "", altText: row.alt_text || "", file: null });
    clearMutationError();
  };

  const submitVersion = async () => {
    try {
      const updated = await createVersionItem(versioningRow.image_id, { version: versionForm.version.trim(), altText: versionForm.altText, file: versionForm.file });
      if (versionForm.file) {
        setPreviewUrls((prev) => {
          if (prev[updated.image_id]) URL.revokeObjectURL(prev[updated.image_id]);
          return { ...prev, [updated.image_id]: URL.createObjectURL(versionForm.file) };
        });
      }
      setVersioningRow(null);
    } catch {
      // dialog stays open (e.g. 409 version conflict, 422 invalid image)
    }
  };

  if (loading) return <CatalogLoadingCard testId="images-loading" />;
  if (error) return <CatalogErrorCard message={error} testId="images-error" />;

  return (
    <div className="space-y-4" data-testid="images-panel">
      <CatalogTable
        testId="images-table"
        title="Images"
        icon={ImageIcon}
        headerAction={
          <Button type="button" size="sm" className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500" onClick={openCreate} data-testid="new-images-button">
            <Plus className="h-4 w-4" /> New Image
          </Button>
        }
        rows={items}
        getRowKey={(row) => row.image_id}
        onRowClick={(row) => setExpandedId(expandedId === row.image_id ? null : row.image_id)}
        emptyTitle="No Images yet"
        emptySubtitle='Create one with "New Image" to get started.'
        columns={[
          {
            key: "name",
            label: "Image",
            render: (row) => (
              <div>
                <div className="text-sm font-medium text-slate-100">{row.name}</div>
                <div className="text-xs text-slate-400">{row.image_id}</div>
              </div>
            ),
          },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`images-status-${row.image_id}`} /> },
          { key: "current_version", label: "Current Version", render: (row) => <span className="font-mono text-sm text-slate-300">{row.current_version || "—"}</span> },
        ]}
      />

      {items
        .filter((row) => row.image_id === expandedId)
        .map((row) => (
          <div key={row.image_id} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`images-detail-${row.image_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-1 gap-4">
                <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-black/20">
                  {previewUrls[row.image_id] ? (
                    <img src={previewUrls[row.image_id]} alt={row.alt_text || row.name} className="h-full w-full object-cover" data-testid={`images-preview-${row.image_id}`} />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-slate-500" data-testid={`images-no-preview-${row.image_id}`} />
                  )}
                </div>
                <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                  <Detail label="Governed ID" value={row.image_id} mono />
                  <Detail label="Status" value={row.status || "—"} />
                  <Detail label="Current Version" value={row.current_version || "—"} mono />
                  <Detail label="Alt Text" value={row.alt_text || "—"} />
                  <Detail label="Content Type" value={row.content_type || "—"} mono />
                  <Detail label="Dimensions" value={row.width && row.height ? `${row.width} × ${row.height}` : "—"} />
                  <Detail label="File Size" value={row.file_size ? `${(row.file_size / 1024).toFixed(1)} KB` : "—"} />
                  <Detail label="Updated" value={row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"} />
                </div>
              </div>
              <div className="flex shrink-0 flex-col gap-2">
                <Button type="button" size="sm" variant="outline" className="gap-1.5 border-white/10 text-slate-200" onClick={() => openEdit(row)} data-testid={`edit-images-${row.image_id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Edit Metadata
                </Button>
                <Button type="button" size="sm" variant="outline" className="gap-1.5 border-violet-400/30 text-violet-200" onClick={() => openVersion(row)} data-testid={`new-version-images-${row.image_id}`}>
                  <History className="h-3.5 w-3.5" /> Create New Version
                </Button>
              </div>
            </div>
            {!previewUrls[row.image_id] && (
              <p className="mt-3 text-xs text-slate-500" data-testid={`images-preview-unavailable-${row.image_id}`}>
                No live preview available for a previously-uploaded image (no image-serving endpoint exists yet -- future seam). Metadata above reflects the actual stored file.
              </p>
            )}
          </div>
        ))}

      <EntityFormDialog
        testId="create-images-dialog"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New Image"
        description="A governed, reusable image (IMG####). File type, dimensions, and size are read from the actual uploaded file -- never typed manually."
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError}
        submitLabel="Create Image"
      >
        <div className="space-y-2">
          <Label htmlFor="images-name">Name</Label>
          <Input id="images-name" value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} required data-testid="images-name-input" />
        </div>
        <StatusSelectField id="images-status" value={createForm.status} onChange={(status) => setCreateForm((f) => ({ ...f, status }))} values={STATUS_VALUES} testId="images-status-select" />
        <div className="space-y-2">
          <Label htmlFor="images-alt-text">Alt Text</Label>
          <Input id="images-alt-text" value={createForm.altText} onChange={(e) => setCreateForm((f) => ({ ...f, altText: e.target.value }))} placeholder='Pass empty for an intentionally decorative image' data-testid="images-alt-text-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="images-file">File</Label>
          <FileField id="images-file" file={createForm.file} onChange={(file) => setCreateForm((f) => ({ ...f, file }))} testId="images-file-input" />
        </div>
      </EntityFormDialog>

      {editingRow && (
        <EntityFormDialog
          testId="edit-images-dialog"
          open={!!editingRow}
          onOpenChange={(open) => !open && setEditingRow(null)}
          title={`Edit ${editingRow.image_id}`}
          description='Rename or change the governed status of this Image. Use "Create New Version" to replace the file.'
          onSubmit={submitEdit}
          submitting={mutating}
          error={mutationError}
          submitLabel="Save Changes"
        >
          <div className="space-y-2">
            <Label htmlFor="images-edit-name">Name</Label>
            <Input id="images-edit-name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} required data-testid="images-edit-name-input" />
          </div>
          <StatusSelectField id="images-edit-status" value={editForm.status} onChange={(status) => setEditForm((f) => ({ ...f, status }))} values={STATUS_VALUES} testId="images-edit-status-select" />
        </EntityFormDialog>
      )}

      {versioningRow && (
        <EntityFormDialog
          testId="new-version-images-dialog"
          open={!!versioningRow}
          onOpenChange={(open) => !open && setVersioningRow(null)}
          title={`New Version — ${versioningRow.image_id}`}
          description="Replaces the underlying image via a new, immutable Version. The current Version's stored file stays historically accessible and unchanged."
          onSubmit={submitVersion}
          submitting={mutating}
          error={mutationError}
          submitLabel="Create New Version"
        >
          <div className="rounded-md border border-white/10 bg-black/20 p-3 text-sm">
            <div className="flex justify-between text-slate-400">
              <span>Asset</span>
              <span className="font-mono text-slate-200" data-testid="new-version-images-asset-id">{versioningRow.image_id}</span>
            </div>
            <div className="mt-1 flex justify-between text-slate-400">
              <span>Current version</span>
              <span className="font-mono text-slate-200" data-testid="new-version-images-current">{versioningRow.current_version || "—"}</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="images-new-version">New Version</Label>
            <Input id="images-new-version" value={versionForm.version} onChange={(e) => setVersionForm((f) => ({ ...f, version: e.target.value }))} placeholder="e.g. 2.0.0" required data-testid="images-new-version-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="images-new-alt-text">Alt Text</Label>
            <Input id="images-new-alt-text" value={versionForm.altText} onChange={(e) => setVersionForm((f) => ({ ...f, altText: e.target.value }))} data-testid="images-new-alt-text-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="images-new-file">Replacement File</Label>
            <FileField id="images-new-file" file={versionForm.file} onChange={(file) => setVersionForm((f) => ({ ...f, file }))} testId="images-new-file-input" />
          </div>
        </EntityFormDialog>
      )}
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
